import React from 'react';
import { useDispatch } from 'react-redux';

import { useAppContext } from '@prisjakt/ui-app-context';

import {
  OutlinedButton,
  ButtonText,
  ButtonIcon,
} from '@prisjakt/ui/button/variants';
import { IconChevronRight } from '@prisjakt/ui/icon';
import {
  BodySmallText,
  H4Text,
  FontEmphasis,
} from '@prisjakt/ui/text/variants';

import { productPriceClick } from 'src/areas/product/actions/product';
import { logRevenueEvent } from 'src/areas/product/components/price-list/price-row/utils';
import { Price } from '../../price';
import { StoreInfo } from './store-info';

import { BestOfferShopData, Maybe } from 'src/types';
import { BaseProps } from '../../../types';
import { CheapestOfferPrice } from './types';

import {
  StyledClickoutWrapper,
  StyledPriceWrapper,
  StyledVerticalLine,
} from './styles';

export type Props = Pick<
  BaseProps,
  'priceCount' | 'price' | 'priceInclShipping'
> & {
  cheapestPrice?: CheapestOfferPrice;
  stockStatus?: Maybe<string>;
  store?: Maybe<BestOfferShopData>;
  productLink?: Maybe<string>;
  storeLink?: Maybe<string>;
  priceId?: Maybe<string>;
  productId?: number;
};

export const ListCardClickout: React.FC<Props> = props => {
  const {
    priceCount,
    price,
    stockStatus,
    store,
    cheapestPrice,
    productLink,
    storeLink,
    priceId,
    productId,
  } = props;
  const { t } = useAppContext();

  const dispatch = useDispatch();

  const handleOnClick = (e: React.MouseEvent) => {
    // Enabling click on clickable buttons inside a clickable container
    e.stopPropagation();
    if (!priceId || !productId) {
      return;
    }

    // Track revenue and event on click out
    logRevenueEvent(String(priceId), productId);

    dispatch(
      productPriceClick(
        productId,
        price,
        true,
        '',
        false,
        'SearchResultPageListCard'
      )
    );
  };

  const renderPrice = () => {
    if (price) {
      return (
        <>
          <BodySmallText emphasis={FontEmphasis.LOW}>
            {t('product', 'price.from-abbr')}{' '}
          </BodySmallText>

          <Price
            price={
              // If the product has a value for cheapestPrice.inclShipping and the "price from" is higher,
              // it's probably due to one of the price values has been cached. Then we set "price from" to cheapestPrice
              // to prevent confusion. This is an edge case.
              cheapestPrice?.inclShipping && cheapestPrice?.inclShipping < price
                ? cheapestPrice?.inclShipping
                : price
            }
            isDeal={false}
          />
        </>
      );
    }
    return (
      <H4Text emphasis={FontEmphasis.HIGH}>
        {t('product', 'price-cta.missing')}
      </H4Text>
    );
  };

  const getCTAText = () => {
    if (priceCount && price) {
      return priceCount > 1
        ? t('product', 'price-cta.see-all-prices')
        : t('product', 'price-cta.see-price');
    }
    return t('product', 'price-cta.see-product');
  };

  return (
    <StyledClickoutWrapper>
      <StoreInfo
        cheapestPrice={cheapestPrice}
        onClickHandler={handleOnClick}
        priceId={priceId}
        stockStatus={stockStatus}
        store={store}
        storeLink={storeLink}
      />
      <StyledVerticalLine />
      <StyledPriceWrapper>
        <div>{renderPrice()}</div>
        <OutlinedButton
          style={{ marginTop: 'auto' }}
          container="a"
          containerProps={{
            href: productLink || '',
          }}
          onClick={e => handleOnClick(e)}
        >
          <ButtonText>{getCTAText()}</ButtonText>
          <ButtonIcon>
            <IconChevronRight />
          </ButtonIcon>
        </OutlinedButton>
      </StyledPriceWrapper>
    </StyledClickoutWrapper>
  );
};
